import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { CartProvider } from "./lib/CartContext";
import { NotificationProvider } from "./lib/NotificationContext";
import { TopBar, BottomNav } from "./components/Layout";
import SmartShopperChat from "./components/SmartShopperChat";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import RegisterPage from "./pages/RegisterPage";
import AuthCallback from "./pages/AuthCallback";
import CategoryPage from "./pages/CategoryPage";
import StorePage from "./pages/StorePage";
import JoinStore from "./pages/JoinStore";
import ProductPage from "./pages/ProductPage";
import LivePage from "./pages/LivePage";
import LiveFeedPage from "./pages/LiveFeedPage";
import VideoFeedPage from "./pages/VideoFeedPage";
import NewLaunchesPage from "./pages/NewLaunchesPage";
import RentalsPage from "./pages/RentalsPage";
import RentalCartPage from "./pages/RentalCartPage";
import MyRentalsPage from "./pages/MyRentalsPage";
import CampaignPage from "./pages/CampaignPage";
import SearchPage from "./pages/SearchPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import { OrdersPage, OrderDetailPage } from "./pages/OrdersPage";
import SellerPage from "./pages/SellerPage";
import StudioPage from "./pages/StudioPage";
import AdminPage from "./pages/AdminPage";
import ProfilePage from "./pages/ProfilePage";
import SavedPage from "./pages/SavedPage";
import FAQPage from "./pages/FAQPage";
import "./App.css";
import "./index.css";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-10 text-center text-[#A1A1AA]">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function KeyedSmartAssistant() {
  // Force a full unmount+remount of the assistant when the authenticated
  // identity (user_id or role) changes. Using a React `key` guarantees
  // fresh state (turns, sessionId, open, input) so no cross-user chat
  // or persona leakage is possible on profile switch — belt & suspenders
  // beyond the in-component useEffect wipe.
  const { user } = useAuth();
  if (!user) return null;
  return <SmartShopperChat key={`${user.user_id}|${user.role || "buyer"}`} />;
}

function AppShell() {
  const loc = useLocation();
  // Intercept OAuth callback URLs at any route
  if (loc.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Hide chrome (TopBar + BottomNav) on full-immersion screens.
  // /live/:id was already chrome-less; /videos joins the club so the TikTok-
  // style swipe feed renders edge-to-edge on mobile.
  const hideChrome = loc.pathname.startsWith("/live/") ||
                     loc.pathname === "/videos" ||
                     loc.pathname.startsWith("/videos/");
  return (
    <div className="min-h-screen bg-[#0F0817] text-[#FAFAFA]">
      {!hideChrome && <TopBar onSearchClick={() => (window.location.href = "/search")} />}
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/admin-login" element={<AdminLoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/category/:id" element={<CategoryPage />} />
        <Route path="/store/:id" element={<StorePage />} />
        <Route path="/join/:id" element={<JoinStore />} />
        <Route path="/product/:id" element={<ProductPage />} />
        <Route path="/live" element={<LiveFeedPage />} />
        <Route path="/live/:id" element={<LivePage />} />
        <Route path="/videos" element={<VideoFeedPage />} />
        <Route path="/new-launches" element={<NewLaunchesPage />} />
        <Route path="/rentals" element={<RentalsPage />} />
        <Route path="/rental-cart" element={<RequireAuth><RentalCartPage /></RequireAuth>} />
        <Route path="/my-rentals" element={<MyRentalsPage />} />
        <Route path="/campaign/:id" element={<CampaignPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<RequireAuth><CheckoutPage /></RequireAuth>} />
        <Route path="/orders" element={<RequireAuth><OrdersPage /></RequireAuth>} />
        <Route path="/orders/:id" element={<RequireAuth><OrderDetailPage /></RequireAuth>} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/saved/:kind" element={<RequireAuth><SavedPage /></RequireAuth>} />
        <Route path="/seller" element={<RequireAuth><SellerPage /></RequireAuth>} />
        <Route path="/seller/studio" element={<RequireAuth><StudioPage /></RequireAuth>} />
        <Route path="/studio" element={<RequireAuth><StudioPage defaultMode="record" /></RequireAuth>} />
        <Route path="/go-live" element={<RequireAuth><StudioPage defaultMode="live" /></RequireAuth>} />
        <Route path="/admin" element={<RequireAuth><AdminPage /></RequireAuth>} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="*" element={<div className="p-10 text-center"><h2 className="font-display text-4xl">404</h2></div>} />
      </Routes>
      <BottomNav />
      {/* Global floating shopping assistant — self-gated to logged-in users.
          Keyed by user_id+role so a profile switch (or any identity change)
          fully unmounts + remounts a fresh instance — no possible state leak. */}
      <KeyedSmartAssistant />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <CartProvider>
            <AppShell />
          </CartProvider>
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
